import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  DocumentIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  ViewColumnsIcon,
  Squares2X2Icon,
  ChevronDownIcon,
  CalendarIcon,
  UserIcon,
  EyeIcon,
  ArrowDownTrayIcon,
  ShareIcon,
  BookmarkIcon,
  HeartIcon,
  UserGroupIcon,
  ClockIcon,
  FolderIcon
} from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid, HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const SharedDocuments = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSharer, setSelectedSharer] = useState('');
  const [activeTab, setActiveTab] = useState('with-me');

  useEffect(() => {
    // Simulate fetching shared documents
    const fetchDocuments = () => {
      const mockDocuments = [
        {
          id: 1,
          title: 'Department Budget 2025',
          fileName: 'dept-budget-2025.xlsx',
          type: 'XLSX',
          category: 'Financial Records',
          size: '2.1 MB',
          uploadDate: '2025-01-22',
          lastModified: '2025-01-23',
          author: 'Jennifer Wu',
          authorAvatar: '/images/avatars/jennifer-wu.jpg',
          sharedBy: 'Jennifer Wu',
          sharedWith: ['Current User', 'Dr. Michael Chen', 'Lisa Rodriguez'],
          sharedDate: '2025-01-23',
          description: 'Annual budget breakdown for the cardiology department.',
          downloads: 34,
          views: 67,
          tags: ['Budget', 'Finance', 'Cardiology'],
          isBookmarked: true,
          isLiked: false,
          shareType: 'with-me', // 'with-me' or 'by-me'
          permissions: {
            canEdit: false,
            canComment: true,
            canShare: true,
            canDownload: true
          }
        },
        {
          id: 2,
          title: 'New Patient Protocol Guidelines',
          fileName: 'patient-protocol-guidelines.pdf',
          type: 'PDF',
          category: 'Medical Procedures',
          size: '1.8 MB',
          uploadDate: '2025-01-20',
          lastModified: '2025-01-21',
          author: 'Dr. Sarah Johnson',
          authorAvatar: '/images/avatars/sarah-johnson.jpg',
          sharedBy: 'Dr. Sarah Johnson',
          sharedWith: ['All Nursing Staff'],
          sharedDate: '2025-01-21',
          description: 'Updated guidelines for handling new patient admissions.',
          downloads: 156,
          views: 289,
          tags: ['Protocol', 'Nursing', 'Admissions'],
          isBookmarked: false,
          isLiked: true,
          shareType: 'with-me',
          permissions: {
            canEdit: false,
            canComment: true,
            canShare: false,
            canDownload: true
          }
        },
        {
          id: 3,
          title: 'Research Methodology Guide',
          fileName: 'research-methodology-guide.doc',
          type: 'DOC',
          category: 'Research',
          size: '3.2 MB',
          uploadDate: '2025-01-18',
          lastModified: '2025-01-19',
          author: 'Current User',
          authorAvatar: '/images/avatars/current-user.jpg',
          sharedBy: 'Current User',
          sharedWith: ['Dr. Emily Davis', 'Dr. Michael Chen', 'Research Team'],
          sharedDate: '2025-01-19',
          description: 'Comprehensive guide for conducting clinical research.',
          downloads: 45,
          views: 89,
          tags: ['Research', 'Methodology', 'Clinical'],
          isBookmarked: false,
          isLiked: false,
          shareType: 'by-me',
          permissions: {
            canEdit: true,
            canComment: true,
            canShare: true,
            canDownload: true
          }
        },
        {
          id: 4,
          title: 'IT Security Update',
          fileName: 'it-security-update-jan2025.pdf',
          type: 'PDF',
          category: 'IT Security',
          size: '1.4 MB',
          uploadDate: '2025-01-15',
          lastModified: '2025-01-16',
          author: 'David Kim',
          authorAvatar: '/images/avatars/david-kim.jpg',
          sharedBy: 'David Kim',
          sharedWith: ['All Staff'],
          sharedDate: '2025-01-16',
          description: 'Important security updates and new protocols.',
          downloads: 234,
          views: 445,
          tags: ['Security', 'IT', 'Updates'],
          isBookmarked: true,
          isLiked: true,
          shareType: 'with-me',
          permissions: {
            canEdit: false,
            canComment: false,
            canShare: true,
            canDownload: true
          }
        },
        {
          id: 5,
          title: 'Training Schedule Q1 2025',
          fileName: 'training-schedule-q1-2025.xlsx',
          type: 'XLSX',
          category: 'Training Materials',
          size: '0.8 MB',
          uploadDate: '2025-01-12',
          lastModified: '2025-01-14',
          author: 'Current User',
          authorAvatar: '/images/avatars/current-user.jpg',
          sharedBy: 'Current User',
          sharedWith: ['HR Team', 'Department Heads'],
          sharedDate: '2025-01-14',
          description: 'First quarter training schedule for all departments.',
          downloads: 78,
          views: 134,
          tags: ['Training', 'Schedule', 'Q1'],
          isBookmarked: false,
          isLiked: false,
          shareType: 'by-me',
          permissions: {
            canEdit: true,
            canComment: true,
            canShare: true,
            canDownload: true
          }
        },
        {
          id: 6,
          title: 'Emergency Contact List',
          fileName: 'emergency-contact-list.doc',
          type: 'DOC',
          category: 'Safety Protocols',
          size: '0.3 MB',
          uploadDate: '2025-01-10',
          lastModified: '2025-01-11',
          author: 'Lisa Rodriguez',
          authorAvatar: '/images/avatars/lisa-rodriguez.jpg',
          sharedBy: 'Lisa Rodriguez',
          sharedWith: ['Security Team', 'Nursing Staff'],
          sharedDate: '2025-01-11',
          description: 'Updated emergency contact information for all staff.',
          downloads: 89,
          views: 167,
          tags: ['Emergency', 'Contacts', 'Safety'],
          isBookmarked: true,
          isLiked: false,
          shareType: 'with-me',
          permissions: {
            canEdit: false,
            canComment: false,
            canShare: false,
            canDownload: true
          }
        }
      ];

      setDocuments(mockDocuments);
      setLoading(false);
    };

    setTimeout(fetchDocuments, 500);
  }, []);

  const filterDocumentsByTab = (docs) => {
    switch (activeTab) {
      case 'with-me':
        return docs.filter(doc => doc.shareType === 'with-me');
      case 'by-me':
        return docs.filter(doc => doc.shareType === 'by-me');
      default:
        return docs;
    }
  };

  const filteredDocuments = filterDocumentsByTab(documents).filter(document => {
    const matchesSearch = document.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         document.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = !selectedType || document.type === selectedType;
    const matchesCategory = !selectedCategory || document.category === selectedCategory;
    const matchesSharer = !selectedSharer || document.sharedBy === selectedSharer;

    return matchesSearch && matchesType && matchesCategory && matchesSharer;
  });

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    let comparison = 0;
    
    switch (sortBy) {
      case 'title':
        comparison = a.title.localeCompare(b.title);
        break;
      case 'sharer':
        comparison = a.sharedBy.localeCompare(b.sharedBy);
        break;
      case 'category':
        comparison = a.category.localeCompare(b.category);
        break;
      case 'size':
        comparison = parseFloat(a.size) - parseFloat(b.size);
        break;
      case 'downloads':
        comparison = a.downloads - b.downloads;
        break;
      default: // date
        comparison = new Date(a.sharedDate) - new Date(b.sharedDate);
    }
    
    return sortOrder === 'asc' ? comparison : -comparison;
  });

  const uniqueTypes = [...new Set(documents.map(doc => doc.type))];
  const uniqueCategories = [...new Set(documents.map(doc => doc.category))];
  const uniqueSharers = [...new Set(documents.map(doc => doc.sharedBy))];

  const handleToggleBookmark = (docId) => {
    setDocuments(docs => docs.map(doc => 
      doc.id === docId ? { ...doc, isBookmarked: !doc.isBookmarked } : doc
    ));
  };

  const handleToggleLike = (docId) => {
    setDocuments(docs => docs.map(doc => 
      doc.id === docId ? { ...doc, isLiked: !doc.isLiked } : doc
    ));
  };

  const tabs = [
    { 
      id: 'with-me', 
      name: 'Shared with Me', 
      count: documents.filter(doc => doc.shareType === 'with-me').length 
    },
    { 
      id: 'by-me', 
      name: 'Shared by Me', 
      count: documents.filter(doc => doc.shareType === 'by-me').length 
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      {/* Custom Header for Shared Documents */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Shared Documents</h1>
                <p className="mt-2 text-sm text-gray-700">
                  Documents shared with you and documents you've shared with others
                </p>
              </div>
              <div className="mt-4 sm:mt-0">
                <Link
                  to="/documents/upload"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  <DocumentIcon className="-ml-1 mr-2 h-4 w-4" />
                  Upload Document
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <UserGroupIcon className="h-4 w-4 mr-2" />
                  {tab.name}
                  <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs">
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Search and Filters */}
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
              {/* Search */}
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Search shared documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center space-x-4">
                {/* Filters Toggle */}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                  Filters
                </button>

                {/* Sort */}
                <div className="relative">
                  <select
                    value={`${sortBy}-${sortOrder}`}
                    onChange={(e) => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field);
                      setSortOrder(order);
                    }}
                    className="block appearance-none bg-white border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm leading-5 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="date-desc">Recently Shared</option>
                    <option value="date-asc">Oldest Shared</option>
                    <option value="title-asc">Title A-Z</option>
                    <option value="title-desc">Title Z-A</option>
                    <option value="sharer-asc">Shared By A-Z</option>
                    <option value="category-asc">Category A-Z</option>
                    <option value="downloads-desc">Most Downloaded</option>
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                    <ChevronDownIcon className="h-4 w-4" />
                  </div>
                </div>

                {/* View Toggle */}
                <div className="flex rounded-md shadow-sm">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`${
                      viewMode === 'grid'
                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                    } relative inline-flex items-center px-3 py-2 rounded-l-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                  >
                    <Squares2X2Icon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`${
                      viewMode === 'list'
                        ? 'bg-blue-50 border-blue-500 text-blue-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:text-gray-700'
                    } relative inline-flex items-center px-3 py-2 -ml-px rounded-r-md border text-sm font-medium focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500`}
                  >
                    <ViewColumnsIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      File Type
                    </label>
                    <select
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Types</option>
                      {uniqueTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Categories</option>
                      {uniqueCategories.map(category => (
                        <option key={category} value={category}>{category}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shared By
                    </label>
                    <select
                      value={selectedSharer}
                      onChange={(e) => setSelectedSharer(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md py-2 pl-3 pr-10 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Sharers</option>
                      {uniqueSharers.map(sharer => (
                        <option key={sharer} value={sharer}>{sharer}</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-end">
                    <button
                      onClick={() => {
                        setSelectedType('');
                        setSelectedCategory('');
                        setSelectedSharer('');
                        setSearchTerm('');
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Clear Filters
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Documents Grid/List */}
        {sortedDocuments.length === 0 ? (
          <div className="text-center py-12">
            <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No shared documents found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {activeTab === 'with-me' ? 
                "No documents have been shared with you yet." : 
                "You haven't shared any documents yet."
              }
            </p>
            {activeTab === 'by-me' && (
              <div className="mt-6">
                <Link
                  to="/documents/upload"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                >
                  Upload and Share Document
                </Link>
              </div>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedDocuments.map((document) => (
              <div key={document.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200">
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center flex-1">
                      <DocumentIcon className="h-8 w-8 text-blue-500" />
                      <div className="ml-3 flex-1 min-w-0">
                        <Link
                          to={`/documents/${document.id}`}
                          className="text-lg font-medium text-gray-900 hover:text-blue-600 block truncate"
                        >
                          {document.title}
                        </Link>
                        <p className="text-sm text-gray-500 truncate">{document.fileName}</p>
                      </div>
                    </div>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800 ml-2">
                      {document.type}
                    </span>
                  </div>
                  
                  <p className="mt-3 text-sm text-gray-600 line-clamp-2">
                    {document.description}
                  </p>
                  
                  <div className="mt-4 flex items-center text-sm text-gray-500">
                    <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    Shared by {document.sharedBy}
                  </div>
                  
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <FolderIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    {document.category}
                  </div>
                  
                  <div className="mt-2 flex items-center text-sm text-gray-500">
                    <ClockIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                    Shared {document.sharedDate}
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center">
                        <EyeIcon className="h-4 w-4 mr-1" />
                        {document.views}
                      </div>
                      <div className="flex items-center">
                        <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                        {document.downloads}
                      </div>
                    </div>
                    <span className="text-xs">{document.size}</span>
                  </div>
                  
                  {/* Shared With */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center text-sm text-gray-500">
                      <UserGroupIcon className="h-4 w-4 mr-2" />
                      <span className="truncate">
                        Shared with {document.sharedWith.slice(0, 2).join(', ')}
                        {document.sharedWith.length > 2 && ` +${document.sharedWith.length - 2} more`}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleToggleLike(document.id)}
                        className="p-1 rounded-full hover:bg-gray-100"
                        title={document.isLiked ? 'Unlike' : 'Like'}
                      >
                        {document.isLiked ? (
                          <HeartIconSolid className="h-4 w-4 text-red-500" />
                        ) : (
                          <HeartIcon className="h-4 w-4 text-gray-400 hover:text-red-500" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleBookmark(document.id)}
                        className="p-1 rounded-full hover:bg-gray-100"
                        title={document.isBookmarked ? 'Remove bookmark' : 'Bookmark'}
                      >
                        {document.isBookmarked ? (
                          <BookmarkIconSolid className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <BookmarkIcon className="h-4 w-4 text-gray-400 hover:text-yellow-500" />
                        )}
                      </button>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {document.permissions.canShare && (
                        <button className="p-1 rounded-full hover:bg-gray-100" title="Share">
                          <ShareIcon className="h-4 w-4 text-gray-400 hover:text-blue-500" />
                        </button>
                      )}
                      {document.permissions.canDownload && (
                        <button className="p-1 rounded-full hover:bg-gray-100" title="Download">
                          <ArrowDownTrayIcon className="h-4 w-4 text-gray-400 hover:text-green-500" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {document.tags && (
                    <div className="mt-3 flex flex-wrap gap-1">
                      {document.tags.slice(0, 3).map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="divide-y divide-gray-200">
              {sortedDocuments.map((document) => (
                <div key={document.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center flex-1 min-w-0">
                      <DocumentIcon className="h-8 w-8 text-blue-500 flex-shrink-0" />
                      <div className="ml-4 flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <Link
                            to={`/documents/${document.id}`}
                            className="text-lg font-medium text-gray-900 hover:text-blue-600 truncate"
                          >
                            {document.title}
                          </Link>
                          <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {document.type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{document.description}</p>
                        <div className="mt-2 flex items-center space-x-6 text-sm text-gray-500">
                          <div className="flex items-center">
                            <UserIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            Shared by {document.sharedBy}
                          </div>
                          <div className="flex items-center">
                            <FolderIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            {document.category}
                          </div>
                          <div className="flex items-center">
                            <CalendarIcon className="flex-shrink-0 mr-1.5 h-4 w-4" />
                            {document.sharedDate}
                          </div>
                          <div className="flex items-center">
                            <EyeIcon className="h-4 w-4 mr-1" />
                            {document.views}
                          </div>
                          <div className="flex items-center">
                            <ArrowDownTrayIcon className="h-4 w-4 mr-1" />
                            {document.downloads}
                          </div>
                          <span>{document.size}</span>
                        </div>
                        
                        {/* Shared With */}
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <UserGroupIcon className="h-4 w-4 mr-2" />
                          <span className="truncate">
                            Shared with {document.sharedWith.slice(0, 3).join(', ')}
                            {document.sharedWith.length > 3 && ` +${document.sharedWith.length - 3} more`}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleToggleLike(document.id)}
                        className="p-2 rounded-full hover:bg-gray-100"
                      >
                        {document.isLiked ? (
                          <HeartIconSolid className="h-4 w-4 text-red-500" />
                        ) : (
                          <HeartIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                      <button
                        onClick={() => handleToggleBookmark(document.id)}
                        className="p-2 rounded-full hover:bg-gray-100"
                      >
                        {document.isBookmarked ? (
                          <BookmarkIconSolid className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <BookmarkIcon className="h-4 w-4 text-gray-400" />
                        )}
                      </button>
                      {document.permissions.canShare && (
                        <button className="p-2 rounded-full hover:bg-gray-100">
                          <ShareIcon className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                      {document.permissions.canDownload && (
                        <button className="p-2 rounded-full hover:bg-gray-100">
                          <ArrowDownTrayIcon className="h-4 w-4 text-gray-400" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <Footer />
    </div>
  );
};

export default SharedDocuments;
